import * as React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { FiClock } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import 'moment/locale/pt';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  QuestionSidebarContainer,
  QuestionSidebarBody,
  QuestionSidebarSection,
  QuestionSidebarTitle,
  QuestionSidebarHighlight,
  QuestionSidebarTime,
  QuestionSidebarClock,
  QuestionSidebarChart,
  QuestionSidebarChartContainer,
  QuestionSidebarLegends,
  QuestionSidebarLegend,
  QuestionSidebarCount,
  // QuestionSidebarYesterday,
  QuestionSidebarHeader
} from '@/components/custom/question';

export function ExercisesQuestionsSidebar({ reply, lastAnswerSubmited }) {
  const { t } = useTranslation();
  const client = useClient();
  const navigate = useNavigate();
  const [currGroupedAnswer, setCurrGroupedAnswer] = React.useState();
  const [currDuration, setCurrDuration] = React.useState();
  const [currDurationInterval, setCurrDurationInterval] = React.useState();
  const [chartOptions, setChartOptions] = React.useState();

  React.useEffect(() => {
    fetchGroupedAnswers();
  }, [lastAnswerSubmited]);

  React.useEffect(() => {
    getChartOptions();
  }, [currGroupedAnswer]);

  const fetchGroupedAnswers = async () => {
    try {
      const { data } = await client.fetch('daily_grouped_answers/last');
      setCurrGroupedAnswer(data);
      if (data && data.status === 'played') {
        getDuration(data);
        setCurrDurationInterval(setInterval(() => getDuration(data), 1000 * 60));
      } else {
        getDuration(data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getDuration = currGroupedAnswer => {
    const diff = moment().unix() - moment(currGroupedAnswer?.currentTimeStart).unix() || 0;
    setCurrDuration(moment.utc(diff * 1000 + (currGroupedAnswer?.duration || 0)));
  };

  const pauseGroupedAnswer = async (newStatus, exit) => {
    if (currGroupedAnswer) {
      const { data } = await client.mutate(['daily_grouped_answers', currGroupedAnswer.id], { status: newStatus });
      setCurrGroupedAnswer(data);
      clearInterval(currDurationInterval);
      if (data && data.status === 'played') {
        getDuration(data);
        setCurrDurationInterval(setInterval(() => getDuration(data), 1000 * 60));
      } else {
        getDuration(data);
      }
    }

    if (exit) {
      navigate('/questions/apostilas');
    }
  };

  const getChartOptions = () => {
    if (currGroupedAnswer) {
      setChartOptions({
        chart: {
          backgroundColor: 'transparent',
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          type: 'pie'
          // width: 44,
          // height: 44,
        },
        colors: ['#32C9D3', '#FD6262'],
        credits: {
          enabled: false
        },
        title: {
          text: null
        },
        tooltip: { enabled: false },
        legend: {
          enabled: false
        },
        xAxis: {
          visible: false
        },
        yAxis: {
          visible: false
        },
        plotOptions: {
          pie: {
            size: '100%',
            dataLabels: { enabled: false }
          }
        },
        series: [
          {
            name: 'Aproveitamento',
            data: [
              {
                name: 'Acertou',
                y: currGroupedAnswer.statistics.correctCount
              },
              {
                name: 'Errou',
                y: currGroupedAnswer.statistics.wrongCount
              }
            ]
          }
        ]
      });
    }
  };

  return (
    <QuestionSidebarContainer>
      {reply && (
        <QuestionSidebarHeader>
          {/*<button className="btn btn--small btn--link btn--full">
            Esconder performance
          </button>*/}
          <Button
            fullWidth
            onClick={() => pauseGroupedAnswer('paused', true)}
          >
            {t('activity.saveAndLeave')}
          </Button>
        </QuestionSidebarHeader>
      )}

      <QuestionSidebarBody>
        <QuestionSidebarSection>
          <QuestionSidebarTitle>{t('activity.titleResponseToday')}</QuestionSidebarTitle>
          <QuestionSidebarChart>
            {reply && chartOptions && (
              <QuestionSidebarChartContainer>
                <HighchartsReact
                  style={{ width: '80px', height: '80px' }}
                  highcharts={Highcharts}
                  options={chartOptions}
                />
              </QuestionSidebarChartContainer>
            )}
            <QuestionSidebarLegends>
              <QuestionSidebarLegend>
                {t('activity.missed')}{' '}
                <QuestionSidebarCount
                  // Define a variante 'error' se a contagem for maior que zero, senão não define a variante.
                  variant={(currGroupedAnswer?.statistics?.wrongCount || 0) > 0 ? 'error' : undefined}
                >
                  {(currGroupedAnswer && currGroupedAnswer?.statistics?.wrongCount) || 0}
                </QuestionSidebarCount>
              </QuestionSidebarLegend>

              <QuestionSidebarLegend>
                {t('activity.right')}{' '}
                <QuestionSidebarCount
                  // Define a variante 'success' se a contagem for maior que zero, senão não define a variante.
                  variant={(currGroupedAnswer?.statistics?.correctCount || 0) > 0 ? 'success' : undefined}
                >
                  {(currGroupedAnswer && currGroupedAnswer?.statistics?.correctCount) || 0}
                </QuestionSidebarCount>
              </QuestionSidebarLegend>
            </QuestionSidebarLegends>
          </QuestionSidebarChart>

          <QuestionSidebarHighlight>
            {t('activity.useOf')} <strong>{(currGroupedAnswer && currGroupedAnswer?.statistics?.hitPercentage) || 0}%</strong>
          </QuestionSidebarHighlight>

          {/* <QuestionSidebarHighlight>
            Questões respondidas: <strong>{(currGroupedAnswer && currGroupedAnswer?.statistics?.totalCount) || 0}</strong>
          </QuestionSidebarHighlight> */}
        </QuestionSidebarSection>

        <QuestionSidebarSection>
          <QuestionSidebarTime>
            <QuestionSidebarClock>
              <FiClock className={currGroupedAnswer && currGroupedAnswer.status == 'played' ? 'animate-spinner' : ''} />
              {currGroupedAnswer ? currDuration?.format('HH:mm') : '00:00'}
            </QuestionSidebarClock>

            {currGroupedAnswer && (
              <Button
                variant="outline"
                size="xs"
                onClick={() => pauseGroupedAnswer(currGroupedAnswer.status === 'played' ? 'paused' : 'played', false)}
              >
                {currGroupedAnswer.status === 'played' ? t('questionBooks.buttonPause') : t('button.continue')}
              </Button>
            )}
          </QuestionSidebarTime>

          <QuestionSidebarHighlight>
            <div dangerouslySetInnerHTML={{ __html: t('activity.warningText') }}></div>
          </QuestionSidebarHighlight>

          {/* {currGroupedAnswer && currGroupedAnswer.lastGroupedAnswer && (
            <QuestionSidebarYesterday>
              <QuestionSidebarTitle>Ontem você fez:</QuestionSidebarTitle>
              <p className="mb-4">
                Questões: <strong>{currGroupedAnswer.lastGroupedAnswer?.statistics?.totalCount}</strong>
              </p>
              <p className="mb-4">
                Aproveitamento: <strong>{currGroupedAnswer.lastGroupedAnswer?.statistics?.hitPercentage}%</strong>
              </p>
              {currGroupedAnswer.lastGroupedAnswer?.duration > 0 && (
                <p className="mb-4">
                  Durante: <strong>{moment.utc(currGroupedAnswer.lastGroupedAnswer?.duration)?.format('HH:mm')}</strong>
                </p>
              )}
            </>
          )} */}
        </QuestionSidebarSection>
      </QuestionSidebarBody>
    </QuestionSidebarContainer>
  );
}
